'use client';

import Button from 'components/buttons/basic';
import Select from 'components/select';
import SvgArrowBackIos from 'components/svgs/arrow-back';
import SvgArrowForward from '@/components/svgs/arrow-forward-old';
import React, { useEffect, useState } from 'react'
import style from './style.module.scss';
import { usePathname, useRouter } from 'next/navigation';
import useTranslation from 'hooks/useTranslate.hook';
import { useIsClient } from 'hooks/useIsClient.hook';

type PaginationType = {
    currentPage: number;
    total: number;
    pageSize: number;
    onChange: Function;
}

type pageSelectType = {
    currentPage: number;
    pageSize: number;
}

const pageSizes = [
    {size: 10},
    {size: 20},
    {size: 50},
    {size: 100}
];

const between = (x: number, min: number, max: number) => {
    return x >= min && x <= max;
}

const Pagination = ({ currentPage = 1, total, pageSize = 10, onChange }: PaginationType) => {
    
    const router = useRouter();
    const pathname = usePathname();
    const { t } = useTranslation();
    const isClient = useIsClient();
    
    const [numberOfButtons, setNumberOfButtons] = useState(0);

    const handleSelect = ({currentPage, pageSize}: pageSelectType) => {
        const query = {
            currentPage: currentPage.toString(),
            pageSize: pageSize.toString()
        };
        
        router.push(`${pathname}?${new URLSearchParams(query).toString()}`);
        onChange({currentPage, pageSize});
    }

    useEffect(() => {
        setNumberOfButtons(Math.floor((total - 1) / pageSize) + 1);
    }, [total, pageSize]);

    useEffect(() => {
        isClient && handleSelect({currentPage: currentPage, pageSize: pageSize});
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage]);
    
    const renderButtons = () => {
        let buttons = [
            <Button 
                icon={<SvgArrowBackIos key="svg-back" />} 
                shape="circle" variant="contained" 
                color="success" key="button-arrow-back"
                disabled={currentPage === 0 ? true : false}
                onClick={() => handleSelect({currentPage: currentPage - 1, pageSize})} 
            />
        ];

        let hiddenBeforeCurrent = 0;
        let hiddenAfterCurrent = 0;
    
        for (let index = 0; index < numberOfButtons; index++) {
            const text = index + 1;
            if (index === 0) {
                buttons.push(
                    <Button 
                        onClick={() => handleSelect({currentPage: index, pageSize})} 
                        text={(text).toString()} key={'button-back'} 
                        variant={currentPage === index ? "outlined" : "text"}
                        color="success"
                        shape="circle"
                    />
                )
            }
            if (index !== 0 && index < (currentPage - 1)) {
                if (hiddenBeforeCurrent < 1) {
                    buttons.push(
                        <span key={'dot-' + index}>.</span>
                    )
                }
                hiddenBeforeCurrent++;
            }
            if (between(index, (currentPage - 1), (currentPage + 1))) {
                if (index !== currentPage && index !== 0 && index !== (numberOfButtons - 1)) {
                    buttons.push(
                        <Button 
                            onClick={() => handleSelect({currentPage: index, pageSize})} 
                            text={(text).toString()} key={'button-range-' + index} 
                            variant={currentPage === index ? "outlined" : "text"} 
                            color="success"
                            shape="circle"
                        />
                    )
                }

                if (index === currentPage && index > 0 && index < (numberOfButtons - 1)) {
                    buttons.push(
                        <Button 
                            onClick={() => handleSelect({currentPage: index, pageSize})} 
                            text={(text).toString()} key={'button-selected-' + index} 
                            variant={currentPage === index ? "outlined" : "text"} 
                            color="success"
                            shape="circle"
                        />
                    )
                }
            }
            
            if (index !== (numberOfButtons - 1) && index > (currentPage + 1)) {
                if (hiddenAfterCurrent < 1) {
                    buttons.push(
                        <span key={'dot-' + index}>.</span>
                    )
                }
                hiddenAfterCurrent++;
            }
            if (index === (numberOfButtons - 1)) {
                buttons.push(
                    <Button 
                        onClick={() => handleSelect({currentPage: index, pageSize})} 
                        text={(text).toString()} key={'button-forward'} 
                        variant={currentPage === index ? "outlined" : "text"}
                        color="success"
                        shape="circle"
                    />
                )
            }
        }

        buttons.push(
            <Button 
                icon={<SvgArrowForward 
                key="svg-right" />} 
                shape="circle" 
                variant="contained" color="success" 
                key="button-arrow-forward"
                disabled={currentPage === (numberOfButtons - 1) ? true : false}
                onClick={() => handleSelect({currentPage: currentPage + 1, pageSize})}
            />
        )

        return buttons;
    }

    if (numberOfButtons < 2) {
        return <></>
    }
    
    return (
        <div className={style.cPagination}>
            <div className={style.cPagination__buttonsWrap}>{numberOfButtons > 0 && renderButtons()}</div>
            <Select 
                title={t('page_size')} 
                data={pageSizes && pageSizes?.map((item: any, index: number) => {
                    return { ...item, id: item.size, name: item.size }
                })} 
                placeholder="Select number of rows"
                defaultValue={pageSize ?? 10} 
                disableTyping
                onChange={(value: any) => {
                    onChange({ currentPage: 0, pageSize: value.name ?? 10 })
                    handleSelect({ currentPage: 0, pageSize: value.name ?? 10 })
                }}
                customClass={`${style.cPagination__select}`}
            />
        </div>
    )
}

export default Pagination